import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SeoHead from '@/components/base/SeoHead';
import { useTheme } from '@/context/ThemeContext';
import { useFaq } from '@/context/FaqContext';
import { useBadgeVisibility } from '@/context/BadgeVisibilityContext';

export default function FaqPage() {
  const { theme } = useTheme();
  const { settings } = useFaq();
  const { settings: badgeSettings } = useBadgeVisibility();
  const light = theme === 'light';

  const [activeCategory, setActiveCategory] = useState<string>('All');
  const [openId, setOpenId] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const bg = light ? 'bg-gray-50' : 'bg-[#0D1B2A]';
  const headingColor = light ? 'text-gray-900' : 'text-white';
  const bodyColor = light ? 'text-gray-600' : 'text-white/60';
  const cardBg = light ? 'bg-white border border-gray-200' : 'bg-[#1C2B3A] border border-white/5';
  const breadcrumbColor = light ? 'text-gray-400' : 'text-white/30';
  const breadcrumbActive = light ? 'text-gray-600' : 'text-white/60';
  const inputBg = light
    ? 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
    : 'bg-[#1C2B3A] border-white/10 text-white placeholder-white/30';
  const catInactive = light
    ? 'bg-white border border-gray-200 text-gray-600 hover:border-[var(--accent)] hover:text-[var(--accent)]'
    : 'bg-[#1C2B3A] border border-white/10 text-white/50 hover:border-[var(--accent)] hover:text-[var(--accent)]';
  const questionColor = light ? 'text-gray-800' : 'text-white';
  const answerColor = light ? 'text-gray-500' : 'text-white/50';
  const dividerColor = light ? 'border-gray-100' : 'border-white/5';

  const publishedFaqs = settings.faqs.filter((f) => f.published);

  const allCategories = ['All', ...settings.categories.filter((c) =>
    publishedFaqs.some((f) => f.category === c)
  )];

  const filtered = useMemo(() => {
    let list = publishedFaqs;
    if (activeCategory !== 'All') {
      list = list.filter((f) => f.category === activeCategory);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (f) =>
          f.question.toLowerCase().includes(q) ||
          f.answer.toLowerCase().includes(q)
      );
    }
    return list;
  }, [publishedFaqs, activeCategory, search]);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: publishedFaqs.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  };

  const toggle = (id: string) => setOpenId((prev) => (prev === id ? null : id));

  return (
    <div className={`min-h-screen ${bg} transition-colors duration-300`}>
      <SeoHead
        title="FAQ | Stingray Marine Batteries — Warranty, Shipping & Technical Questions"
        description={settings.pageSubtitle}
        keywords="Stingray FAQ, marine battery warranty, LiFePO4 questions, AGM battery FAQ, marine battery shipping, dealer program"
        canonical="https://stingray-marine.com/faq"
        schema={faqSchema}
      />
      <Navbar />

      {/* Header */}
      <header className={`relative pt-32 pb-16 overflow-hidden transition-colors duration-300 ${light ? 'bg-gray-100' : 'bg-[#0D1B2A]'}`}>
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://readdy.ai/api/search-image?query=abstract%20dark%20navy%20ocean%20water%20texture%20deep%20sea%20background%20minimal%20dark%20blue%20waves%20pattern%20professional%20moody%20atmosphere%20high%20resolution%20seamless%20background&width=1440&height=400&seq=faq-header-bg&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="relative z-10 w-full px-6 md:px-10">
          <nav aria-label="Breadcrumb" className={`flex items-center gap-2 text-xs ${breadcrumbColor} mb-4`}>
            <Link to="/" className="hover:text-[var(--accent)] transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className={breadcrumbActive}>FAQ</span>
          </nav>
          {badgeSettings.faqBadge && (
            <span
              className="inline-block text-xs font-bold tracking-widest uppercase px-4 py-1.5 rounded-full mb-4"
              style={{ backgroundColor: 'color-mix(in srgb, var(--accent) 20%, transparent)', border: '1px solid color-mix(in srgb, var(--accent) 40%, transparent)', color: 'var(--accent)' }}
            >
              Help Center
            </span>
          )}
          <h1 className={`text-4xl md:text-5xl font-black ${headingColor} mb-4`}>
            {settings.pageTitle}
          </h1>
          <p className={`${bodyColor} text-lg max-w-2xl`}>
            {settings.pageSubtitle}
          </p>
        </div>
      </header>

      <main className="w-full px-6 md:px-10 pb-24">
        {/* Search + Stats */}
        <div className="pt-10 pb-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="relative w-full sm:w-80">
            <i className={`ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm ${light ? 'text-gray-400' : 'text-white/30'}`}></i>
            <input
              type="text"
              placeholder="Search questions..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={`w-full border rounded-lg pl-9 pr-4 py-2.5 text-sm outline-none transition-colors focus:border-[var(--accent)] ${inputBg}`}
            />
          </div>
          <p className={`text-sm ${bodyColor} shrink-0`}>
            {filtered.length} question{filtered.length !== 1 ? 's' : ''} found
          </p>
        </div>

        {/* Category filter */}
        <div className="flex flex-wrap gap-2 mb-8">
          {allCategories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                activeCategory === cat ? 'text-white' : catInactive
              }`}
              style={activeCategory === cat ? { backgroundColor: 'var(--accent)', borderColor: 'var(--accent)' } : {}}
            >
              {cat}
              {cat !== 'All' && (
                <span className={`ml-1.5 text-[10px] ${activeCategory === cat ? 'opacity-70' : 'opacity-50'}`}>
                  ({publishedFaqs.filter((f) => f.category === cat).length})
                </span>
              )}
            </button>
          ))}
        </div>

        {/* FAQ Accordion */}
        <div className="max-w-3xl">
          {filtered.length === 0 ? (
            <div className={`text-center py-20 ${bodyColor}`}>
              <i className="ri-question-line text-5xl mb-4 block opacity-30"></i>
              <p className="font-semibold">No questions found</p>
              <p className="text-sm mt-1 opacity-60">Try a different search term or category</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filtered.map((faq, idx) => {
                const isOpen = openId === faq.id;
                return (
                  <div
                    key={faq.id}
                    className={`${cardBg} rounded-lg overflow-hidden transition-all duration-200`}
                    style={isOpen ? { borderColor: 'color-mix(in srgb, var(--accent) 40%, transparent)' } : {}}
                  >
                    <button
                      onClick={() => toggle(faq.id)}
                      className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left cursor-pointer group"
                    >
                      <div className="flex items-start gap-3">
                        <span
                          className="text-xs font-black shrink-0 mt-0.5 w-5 h-5 flex items-center justify-center rounded-full"
                          style={{ backgroundColor: 'color-mix(in srgb, var(--accent) 15%, transparent)', color: 'var(--accent)' }}
                        >
                          {idx + 1}
                        </span>
                        <span className={`font-semibold text-sm leading-snug ${questionColor} group-hover:text-[var(--accent)] transition-colors`}>
                          {faq.question}
                        </span>
                      </div>
                      <div
                        className="w-6 h-6 flex items-center justify-center rounded-full shrink-0 transition-all duration-200"
                        style={isOpen ? { backgroundColor: 'var(--accent)' } : { backgroundColor: 'color-mix(in srgb, var(--accent) 10%, transparent)' }}
                      >
                        <i
                          className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                          style={{ color: isOpen ? 'white' : 'var(--accent)' }}
                        ></i>
                      </div>
                    </button>

                    {isOpen && (
                      <div className={`px-6 pb-5 border-t ${dividerColor}`}>
                        <div className="flex items-start gap-3 pt-4">
                          <span className="w-5 h-5 shrink-0 mt-0.5"></span>
                          <p className={`text-sm leading-relaxed ${answerColor}`}>{faq.answer}</p>
                        </div>
                        <div className="flex items-center gap-2 mt-3 pl-8">
                          <span
                            className="text-[10px] font-bold px-2.5 py-0.5 rounded-full"
                            style={{ backgroundColor: 'color-mix(in srgb, var(--accent) 10%, transparent)', color: 'var(--accent)' }}
                          >
                            {faq.category}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Still have questions CTA */}
        <div className={`mt-16 max-w-3xl rounded-xl p-8 ${cardBg}`}>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
            <div
              className="w-14 h-14 flex items-center justify-center rounded-xl shrink-0"
              style={{ backgroundColor: 'color-mix(in srgb, var(--accent) 15%, transparent)' }}
            >
              <i className="ri-customer-service-2-line text-2xl" style={{ color: 'var(--accent)' }}></i>
            </div>
            <div className="flex-1">
              <h3 className={`font-bold text-lg ${headingColor} mb-1`}>Still have questions?</h3>
              <p className={`text-sm ${bodyColor}`}>
                Can&apos;t find what you&apos;re looking for? Our team is happy to help — reach out directly.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 shrink-0">
              <Link
                to="/contact"
                className="flex items-center gap-2 text-white font-bold px-5 py-2.5 rounded-lg text-sm transition-colors whitespace-nowrap cursor-pointer"
                style={{ backgroundColor: 'var(--accent)' }}
              >
                <i className="ri-mail-send-line"></i>
                Contact Us
              </Link>
              <Link
                to="/warranty"
                className={`flex items-center gap-2 font-bold px-5 py-2.5 rounded-lg text-sm transition-colors whitespace-nowrap cursor-pointer border ${
                  light ? 'border-gray-200 text-gray-700 hover:bg-gray-50' : 'border-white/10 text-white hover:bg-white/5'
                }`}
              >
                <i className="ri-shield-check-line"></i>
                Warranty Claims
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
